import * as React from "react";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { View, StyleSheet } from "react-native";
import { primary, textLight } from "@bo/constants/colors";
import { useFocusEffect } from "@react-navigation/native";

import DashboardTemplate from "./template/Template";
import Text from "@bo/components/common/Text";
import { ActivityCard } from "@bo/components/molecule/ActivityCard";
import { NotificationResponse } from "@bo/types";
import { useGetNotifications, usePutMarkNotificationAsRead } from "../../services/notificationsService";
import { useUnreadNotificationsContext } from "../../hooks/unreadNotificationsCountHook";
import { useIsMobile } from "../../hooks/mobileHook";
import Toast from "react-native-toast-message";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { NavigationParamList } from "../../navigation/RootStack";

const desktopStyles = StyleSheet.create({
  titleContainer: {
    paddingHorizontal: 64,
    paddingBottom: 32,
    borderColor: textLight,
    borderBottomWidth: 1,
  },
  contentContainer: {
    paddingTop: 32,
    gap: 32,
  },
  divider: {
    borderColor: textLight,
    borderBottomWidth: 1,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 10,
    backgroundColor: primary,
  },
});

const mobileStyles = StyleSheet.create({
  titleContainer: {
    paddingHorizontal: 32,
    paddingBottom: 16,
    borderColor: textLight,
    borderBottomWidth: 1,
  },
  contentContainer: {
    paddingTop: 16,
    gap: 16,
  },
  divider: {
    borderColor: textLight,
    borderBottomWidth: 1,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 10,
    backgroundColor: primary,
  },
});

export default function NotificationsScreen() {
  const navigation = useNavigation<NavigationProp<NavigationParamList>>();
  const { i18n } = useTranslation();
  const locale = i18n.language === "sl" ? "sl-SI" : "en-US";
  const isMobile = useIsMobile();

  const styles = isMobile ? mobileStyles : desktopStyles;

  const [notifications, setNotifications] = React.useState<NotificationResponse[]>([]);

  const { count, setCount } = useUnreadNotificationsContext();

  const { refetch: fetchNotifications, data: notificationData, error: notificationError } = useGetNotifications();

  const { mutate: markNotificationAsRead } = usePutMarkNotificationAsRead(() => {
    Toast.show({
      type: "error",
      text1: t("toasts.default-error-title"),
      text2: t("toasts.default-error-message", { description: "označevanju obvestila kot prebranega" }),
    });
  });

  useFocusEffect(
    React.useCallback(() => {
      fetchNotifications();
    }, []),
  );

  React.useEffect(() => {
    if (notificationError) {
      Toast.show({
        type: "error",
        text1: t("toasts.default-error-title"),
        text2: t("toasts.default-error-message", { description: "pridobivanju obvestil" }),
      });
    }
  }, [notificationError]);

  React.useEffect(() => {
    if (notificationData) {
      setNotifications(notificationData.data);
      setCount(notificationData.data.filter((notification) => !notification.read).length);
    }
  }, [notificationData]);

  const handleOnPress = (index: number) => {
    if (notifications[index].read) {
      return;
    }

    const newNotifications = [...notifications];
    newNotifications[index].read = true;

    markNotificationAsRead(newNotifications[index].id);
    setNotifications(newNotifications);
    setCount(count - 1);
  };

  return (
    <DashboardTemplate>
      <View style={styles.titleContainer}>
        <Text type="H3">{t("screens.dashboard.notifications.title")}</Text>
      </View>
      <View style={styles.contentContainer}>
        {notifications?.map((notification, index) => {
          console.log("--> notification: ", notification);
          return (
            <View key={notification.id}>
              <ActivityCard
                key={index}
                title={notification.title ?? t("screens.dashboard.notifications.notification")}
                description={`${new Date(notification.time).toLocaleDateString(locale)} ${new Date(
                  notification.time,
                ).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })} - ${notification.text}`}
                icon={!notification.read ? <View style={styles.dot} /> : <></>}
                onCheckedChange={() => handleOnPress(index)}
                checked={notification.read}
                isCheckboxDisabled={notification.read}
                isCheckboxHidden={true}
                onPress={() => {
                  handleOnPress(index);
                  if (notification.appointmentId) {
                    navigation.navigate("Appointment", { id: notification.appointmentId });
                  } else {
                    console.warn("Appointment ID is undefined");
                  }
                }}
              />

              {index + 1 < notifications.length && <View style={styles.divider} />}
            </View>
          );
        })}
      </View>
    </DashboardTemplate>
  );
}
